(function () {
  function normalizeDiscountCode(value) {
    return String(value || '').trim();
  }

  function readDiscountCodes(cart) {
    const values = [];
    const raw = cart?.discount_codes || cart?.discountCodes || [];
    if (Array.isArray(raw)) {
      raw.forEach(function (item) {
        values.push(typeof item === 'string' ? item : item?.code || item?.title || '');
      });
    }
    const applications = []
      .concat(Array.isArray(cart?.cart_level_discount_applications) ? cart.cart_level_discount_applications : [])
      .concat(Array.isArray(cart?.discount_applications) ? cart.discount_applications : []);
    applications.forEach(function (item) {
      values.push(item?.code || item?.title || item?.name || '');
    });
    return values.filter(Boolean).reduce(function (unique, value) {
      const code = normalizeDiscountCode(value);
      if (!unique.some(function (existing) { return existing.toUpperCase() === code.toUpperCase(); })) unique.push(code);
      return unique;
    }, []);
  }

  function confirmDiscountApplied(cart, code) {
    const expected = normalizeDiscountCode(code).toUpperCase();
    if (!expected) return false;
    const explicit = [];
    const raw = cart?.discount_codes || cart?.discountCodes || [];
    if (Array.isArray(raw)) raw.forEach(function (item) {
      if (typeof item === 'object' && item?.code && item.applicable !== undefined) explicit.push(item);
    });
    const matchingExplicit = explicit.find(function (item) { return String(item.code).toUpperCase() === expected; });
    if (matchingExplicit) return matchingExplicit.applicable === true;
    return readDiscountCodes(cart).some(function (item) { return item.toUpperCase() === expected; });
  }

  async function getCart() {
    const response = await fetch(window.Shopify.routes.root + 'cart.js', {headers: {Accept: 'application/json'}});
    if (!response.ok) throw new Error('Unable to read cart');
    return response.json();
  }

  async function updateAttributes(attributes) {
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({attributes: attributes || {}})
    });
    if (!response.ok) throw new Error('Unable to update cart attributes');
    return response.json();
  }

  function parseKeptpointCoupons(attributes) {
    try {
      const parsed = JSON.parse(String(attributes?.['Keptpoint Coupons'] || '[]'));
      if (!Array.isArray(parsed)) return [];
      return parsed.map(function (coupon) {
        if (!coupon || typeof coupon !== 'object') return null;
        const refId = String(coupon.refId || '');
        const discountCode = normalizeDiscountCode(coupon.shopifyCode || coupon.discountCode);
        if (!refId || !discountCode) return null;
        return Object.assign({}, coupon, {refId: refId, shopifyCode: discountCode});
      }).filter(Boolean);
    } catch (_error) {
      return [];
    }
  }

  function upsertKeptpointCoupon(coupons, transaction) {
    const nextCoupon = {
      shopifyCode: transaction.discountCode,
      refId: transaction.refId,
      value: Number(transaction.value || transaction.amount || transaction.discountAmount || 0),
      expiresAt: String(transaction.expiresAt || '')
    };
    if (transaction.kind === 'reward') nextCoupon.redeemCode = transaction.rewardCode || transaction.discountCode;
    else nextCoupon.point = Number(transaction.point || transaction.points || 0);

    const existingIndex = coupons.findIndex(function (coupon) { return coupon.refId === nextCoupon.refId; });
    if (existingIndex < 0) return coupons.concat(nextCoupon);
    return coupons.map(function (coupon, index) { return index === existingIndex ? nextCoupon : coupon; });
  }

  function getPointAllocation(coupons) {
    return (coupons || []).filter(function (coupon) { return !coupon.redeemCode; }).reduce(function (allocation, coupon) {
      allocation.pointUsed += Number(coupon.point || 0);
      allocation.pointAmount += Number(coupon.value || 0);
      return allocation;
    }, {pointUsed: 0, pointAmount: 0});
  }

  function clearKeptpointAttributes(attributes) {
    return Object.assign({}, attributes || {}, {
      'Keptpoint Reservation Scope': null,
      'Keptpoint Reservation IDs': null,
      'Keptpoint Discount Codes': null,
      'Keptpoint Coupons': null,
      'Point Used': null,
      'Point Amount': null,
      'Point Code Ref': null
    });
  }

  function buildKeptpointAttributes(attributes, coupons) {
    if (!coupons.length) return clearKeptpointAttributes(attributes);
    const allocation = getPointAllocation(coupons);
    const pointCoupon = coupons.find(function (coupon) { return !coupon.redeemCode; });
    return Object.assign({}, attributes || {}, {
      'Keptpoint Reservation IDs': JSON.stringify(coupons.map(function (coupon) { return coupon.refId; })),
      'Keptpoint Discount Codes': JSON.stringify(coupons.map(function (coupon) { return coupon.shopifyCode; })),
      'Keptpoint Coupons': JSON.stringify(coupons),
      'Point Used': allocation.pointUsed > 0 ? String(allocation.pointUsed) : null,
      'Point Amount': allocation.pointAmount > 0 ? String(allocation.pointAmount) : null,
      'Point Code Ref': pointCoupon?.refId || null
    });
  }

  function getOriginalLinePrice(item) {
    const originalLinePrice = Number(item?.original_line_price);
    if (Number.isFinite(originalLinePrice)) return originalLinePrice;
    const originalPrice = Number(item?.original_price);
    const quantity = Number(item?.quantity) || 0;
    return Number.isFinite(originalPrice) ? originalPrice * quantity : 0;
  }

  function getOriginalUnitPrice(item) {
    const originalPrice = Number(item?.original_price);
    if (Number.isFinite(originalPrice)) return originalPrice;
    const quantity = Number(item?.quantity) || 0;
    const originalLinePrice = Number(item?.original_line_price);
    return quantity > 0 && Number.isFinite(originalLinePrice) ? originalLinePrice / quantity : 0;
  }

  function getPointLineProperties(cart, item) {
    const coupons = parseKeptpointCoupons(cart?.attributes);
    const allocation = getPointAllocation(coupons);
    const subtotal = (cart?.items || []).reduce(function (total, line) {
      return total + getOriginalLinePrice(line);
    }, 0);
    const properties = Object.assign({}, item.properties || {});
    delete properties._PointAmountForItem;
    delete properties['Point Amount For Item'];
    delete properties['Point Used For Item'];
    if (allocation.pointUsed <= 0 || allocation.pointAmount <= 0 || subtotal <= 0) return properties;

    const ratio = getOriginalLinePrice(item) / subtotal;
    const rawPointAmount = ratio * allocation.pointAmount;
    properties._PointAmountForItem = String(rawPointAmount);
    properties['Point Amount For Item'] = String(Math.round(rawPointAmount));
    properties['Point Used For Item'] = String(ratio * allocation.pointUsed);
    return properties;
  }

  async function syncPointLineProperties(cart) {
    let updatedCart = cart;
    for (const item of cart?.items || []) {
      const properties = getPointLineProperties(updatedCart, item);
      if (JSON.stringify(properties) === JSON.stringify(item.properties || {})) continue;
      const response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
        method: 'POST',
        headers: {'Content-Type': 'application/json', Accept: 'application/json'},
        body: JSON.stringify({id: item.key, quantity: item.quantity, properties: properties})
      });
      if (!response.ok) throw new Error('Unable to update point line properties');
      updatedCart = await response.json();
    }
    return updatedCart;
  }
  async function removeExpiredKeptpointDiscounts(cartOverride) {
    const cart = cartOverride || await getCart();
    const coupons = parseKeptpointCoupons(cart.attributes);
    const expiredCodes = coupons.reduce(function (codes, coupon) {
      const expiresAt = Date.parse(coupon.expiresAt || '');
      if (!Number.isNaN(expiresAt) && expiresAt <= Date.now()) codes.add(coupon.shopifyCode.toUpperCase());
      return codes;
    }, new Set());
    if (!expiredCodes.size) return cart;

    const remainingCoupons = coupons.filter(function (coupon) {
      return !expiredCodes.has(coupon.shopifyCode.toUpperCase());
    });
    const remainingCodes = readDiscountCodes(cart).filter(function (code) {
      return !expiredCodes.has(code.toUpperCase());
    });
    const expiredReward = coupons.some(function (coupon) {
      return expiredCodes.has(coupon.shopifyCode.toUpperCase()) && coupon.redeemCode;
    });
    const attributes = Object.assign({}, buildKeptpointAttributes(cart.attributes, remainingCoupons), expiredReward ? {
      redeemNo: null
    } : {});
    return updateCart(cart, remainingCodes, attributes);
  }

  async function updateCart(cart, codes, attributes) {
    const nextCodes = codes.reduce(function (unique, value) {
      const code = normalizeDiscountCode(value);
      if (code && !unique.some(function (existing) { return existing.toUpperCase() === code.toUpperCase(); })) unique.push(code);
      return unique;
    }, []);
    const response = await fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({discount: nextCodes.join(','), attributes: attributes || cart?.attributes || {}})
    });
    if (!response.ok) throw new Error('Unable to update cart discounts');
    return syncPointLineProperties(await response.json());
  }

  const ajaxCart = {
    getCart: getCart,
    readDiscountCodes: readDiscountCodes,
    updateAttributes: updateAttributes,
    updateDiscountCodes: function (codes, attributes) { return updateCart(null, codes, attributes); },
    applyDiscountCode: function (code, attributes) {
      return getCart().then(function (cart) { return updateCart(cart, readDiscountCodes(cart).concat(code), attributes); });
    },
    removeDiscountCode: function (code, attributes) {
      return getCart().then(function (cart) {
        const target = normalizeDiscountCode(code).toUpperCase();
        return updateCart(cart, readDiscountCodes(cart).filter(function (item) { return item.toUpperCase() !== target; }), attributes);
      });
    },
    confirmDiscountApplied: confirmDiscountApplied,
    parseKeptpointCoupons: parseKeptpointCoupons,
    buildKeptpointAttributes: buildKeptpointAttributes,
    clearKeptpointAttributes: clearKeptpointAttributes,
    syncPointLineProperties: syncPointLineProperties,
    removeExpiredKeptpointDiscounts: removeExpiredKeptpointDiscounts
  };
  window.KeptpointAjaxCart = ajaxCart;

  function setElementHidden(element, hidden) {
    if (!element) return;
    element.hidden = hidden;
    element.style.display = hidden ? 'none' : '';
  }

  function parseJson(value, fallback) {
    try {
      return value ? JSON.parse(value) : fallback;
    } catch (error) {
      return fallback;
    }
  }

  function formatNumber(value) {
    const number = Number(value);
    if (Number.isNaN(number) || number < 0) return '0';
    return new Intl.NumberFormat(window.Shopify?.locale || 'en').format(number);
  }

  function formatMoneyFromCents(cents, moneyFormat) {
    const amount = Number(cents || 0);
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = moneyFormat || '${{amount}}';

    function withDelimiters(number, precision, thousands, decimal) {
      if (Number.isNaN(number) || number == null) return '0';
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1' + thousands);
      const remainder = parts[1] ? decimal + parts[1] : '';
      return dollars + remainder;
    }

    const match = formatString.match(placeholderRegex);
    if (!match) return formatString;

    let value = '';
    switch (match[1]) {
      case 'amount':
        value = withDelimiters(amount, 2, ',', '.');
        break;
      case 'amount_no_decimals':
        value = withDelimiters(amount, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        value = withDelimiters(amount, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = withDelimiters(amount, 0, '.', ',');
        break;
      default:
        value = withDelimiters(amount, 2, ',', '.');
    }

    return formatString.replace(placeholderRegex, value);
  }

  function resolveValue(source, keys, fallback) {
    if (!source) return fallback;
    for (let index = 0; index < keys.length; index += 1) {
      const key = keys[index];
      if (source[key] !== undefined && source[key] !== null && source[key] !== '') return source[key];
    }
    return fallback;
  }

  function getResponseData(payload) {
    return payload?.data?.raw?.data || payload?.data?.raw || payload?.data || payload || {};
  }

  function getCartToken(cart) {
    const token = cart?.token || cart?.cart_token;
    if (!token) throw new Error('Cart token is required');
    return String(token);
  }

  async function ensureReservationScope(cart) {
    const existing = cart?.attributes?.['Keptpoint Reservation Scope'];
    if (existing) return {cart: cart, reservationScopeKey: String(existing)};
    const reservationScopeKey = getCartToken(cart);
    const attributes = Object.assign({}, cart.attributes || {}, {'Keptpoint Reservation Scope': reservationScopeKey});
    const updatedCart = await ajaxCart.updateAttributes(attributes);
    return {cart: updatedCart, reservationScopeKey: String(updatedCart?.attributes?.['Keptpoint Reservation Scope'] || reservationScopeKey)};
  }

  function createApplyRequestId() {
    return window.crypto?.randomUUID ? window.crypto.randomUUID() : String(Date.now()) + '-' + Math.random().toString(16).slice(2);
  }


  function normalizeCoupon(item, moneyFormat) {

    const code = resolveValue(item, ['redeemNo', 'redeem_no', 'code', 'coupon_code', 'referenceNo', 'reference', 'rewardId', 'reward_id', 'id'], '');
    const id = resolveValue(item, ['id', 'discount_id', 'coupon_id', 'rewardId', 'reward_id', 'referenceNo', 'reference'], code);
    const expiresAt = resolveValue(item, ['expires_at', 'expired_at', 'ends_at', 'expire_at'], '');
    const discountText = resolveValue(item, ['discount_text', 'value_text', 'amount_text'], '');
    const price = resolveValue(item, ['price'], {}) || {};
    const discountAmount = resolveValue(item, ['discount_amount', 'amount', 'value'], '') || resolveValue(price, ['discountPrice', 'discount_price'], '');
    const isApplied = Boolean(resolveValue(item, ['is_applied', 'applied', 'selected', 'active'], false));

    return {
      id: String(id || ''),
      code: String(code || ''),
      title: resolveValue(item, ['title', 'name', 'rewardName', 'reward_name', 'couponType', 'type'], code || ''),
      description: resolveValue(item, ['description', 'descriptionShipping', 'subtitle', 'couponType', 'type'], ''),
      expiresAt: expiresAt || resolveValue(item, ['usingPeriod'] , {})?.endDate || resolveValue(item, ['expiredAt', 'validUntil'], ''),
      discountText: discountText || (discountAmount !== '' ? formatMoneyFromCents(discountAmount, moneyFormat) : ''),
      isApplied,
      status: item.status
    };
  }

  function normalizeActiveDiscount(payload, moneyFormat) {
    if (!payload) return null;

    return {
      title: resolveValue(payload, ['title', 'name', 'code'], ''),
      subtitle:
        resolveValue(payload, ['subtitle', 'description'], '') ||
        resolveValue(payload, ['amount_text'], '') ||
        (resolveValue(payload, ['amount', 'discount_amount'], '') !== ''
          ? formatMoneyFromCents(resolveValue(payload, ['amount', 'discount_amount'], 0), moneyFormat)
          : ''),
      expiresAt: resolveValue(payload, ['expires_at', 'ends_at', 'expire_at'], ''),
      type: resolveValue(payload, ['type'], ''),
      pointsApplied: Number(resolveValue(payload, ['points_applied', 'used_points'], 0)) || 0
    };
  }

  function normalizeWallet(payload, moneyFormat) {
    const source = payload?.data || payload?.result || payload || {};
    const rawCoupons = resolveValue(source, ['items', 'reward_coupons', 'coupons', 'wallet', 'rewards'], []);
    const activeDiscount = normalizeActiveDiscount(resolveValue(source, ['active_discount', 'activeDiscount'], null), moneyFormat);
    const nearlyExpire = resolveValue(source, ['nearlyExpire', 'nearly_expire'], null);
    const nearlyExpirePoint =
      (nearlyExpire && typeof nearlyExpire === 'object' ? resolveValue(nearlyExpire, ['point', 'points'], 0) : nearlyExpire) ||
      resolveValue(source, ['nearlyExpirePoint', 'expiring_point', 'expiring_points', 'expiringPoints'], 0);
    const balance =
      resolveValue(source, ['balance'], null) ??
      resolveValue(source, ['pointBalance'], null) ??
      resolveValue(source, ['point_available', 'points', 'available_points', 'pointTotal'], 0);

    return {
      points: Number(balance) || 0,
      expiringPoints: Number(nearlyExpirePoint) || 0,
      memberId: resolveValue(source, ['member_id', 'memberId'], ''),
      coupons: Array.isArray(rawCoupons) ? rawCoupons.map(function (item) { return normalizeCoupon(item, moneyFormat); }) : [],
      activeDiscount
    };
  }

  function normalizeRewards(payload, moneyFormat) {
    const responseData = payload?.data && typeof payload.data === 'object' ? payload.data : payload || {};
    const raw = responseData?.raw && typeof responseData.raw === 'object' ? responseData.raw : {};
    const source = Array.isArray(responseData)
      ? responseData
      : Array.isArray(responseData.items)
        ? responseData
        : Array.isArray(raw.data)
          ? raw
          : getResponseData(payload);
    const rawRewards = Array.isArray(source)
      ? source
      : resolveValue(source, ['items', 'reward_coupons', 'coupons', 'wallet', 'rewards', 'records', 'list', 'data'], []);
    const activeDiscount = normalizeActiveDiscount(resolveValue(source, ['active_discount', 'activeDiscount'], null), moneyFormat);

    return {
      coupons: Array.isArray(rawRewards)
        ? rawRewards.map(function (item) { return normalizeCoupon(item, moneyFormat); }).filter(function (coupon) { return coupon.code; })
        : [],
      activeDiscount: activeDiscount
    };
  }

  function buildProxyUrl(widget, path) {
    return String(widget.dataset.proxyUrl || '').replace(/\/$/, '') + path;
  }

  async function getAppliedTransaction(widget, checkoutId, reservationScopeKey, refId) {
    const response = await requestJson(buildProxyUrl(widget, '/state'), buildRequestBody(widget, {
      checkoutId: checkoutId,
      reservationScopeKey: reservationScopeKey,
      customerId: widget.dataset.customerId || '',
      shopDomain: window.Shopify?.shop || ''
    }));
    const transaction = (response?.data?.transactions || []).find(function (item) {
      return (item.refId === refId || item.id === refId) && item.state === 'APPLIED';
    });
    if (!transaction) throw new Error('Reservation was not applied');
    return transaction;
  }
  function formatCountdown(msRemaining) {
    const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return minutes + ':' + seconds;
  }

  function setLoading(widget, loading) {
    widget.classList.toggle('kp-loyalty--loading', Boolean(loading));
    const loadingEl = widget.querySelector('[data-kp-loading]');
    const content = widget.querySelectorAll('[data-kp-content]');
    setElementHidden(loadingEl, !loading);
    content.forEach(function (element) { setElementHidden(element, Boolean(loading)); });
  }

  function setSectionLoading(widget, sections, loading) {
    const wanted = Array.isArray(sections) ? sections : [sections];
    wanted.forEach(function (section) {
      widget.querySelectorAll('[data-kp-section="' + section + '"]').forEach(function (sectionEl) {
        if (loading && section === 'active') setElementHidden(sectionEl, false);
        sectionEl.classList.toggle('kp-loyalty__section--loading', Boolean(loading));
        sectionEl.querySelectorAll('[data-kp-section-skeleton]').forEach(function (skeleton) {
          setElementHidden(skeleton, !loading);
        });
        sectionEl.querySelectorAll(':scope > :not([data-kp-section-skeleton]), :scope > .kp-loyalty__card-body > :not([data-kp-section-skeleton])').forEach(function (content) {
          content.classList.toggle('kp-loyalty__section-content--loading', Boolean(loading));
        });
      });
    });
  }

  function setDiscountLimitState(widget, text, discountCount) {
    const limitReached = Number(discountCount) >= 5;
    const limitMessage = widget.querySelector('[data-kp-discount-limit-message]');
    if (limitMessage) {
      limitMessage.textContent = limitReached ? text.maxDiscountCodes : '';
      setElementHidden(limitMessage, !limitReached);
    }
    widget.dataset.discountLimitReached = limitReached ? 'true' : 'false';
    return limitReached;
  }

  function setBusy(widget, busy, text) {
    widget.dataset.busy = busy ? 'true' : 'false';
    widget.querySelectorAll('button, input, select').forEach(function (element) {
      element.disabled = Boolean(busy);
    });

    if (!busy) {
      const state = widget._kpState || {};
      const appliedTransactions = Array.isArray(state.appliedTransactions) ? state.appliedTransactions : [];
      const pointTransaction = appliedTransactions.find(function (transaction) {
        return transaction.kind === 'point';
      });
      const rewardApplied = appliedTransactions.some(function (transaction) {
        return transaction.kind !== 'point';
      });
      const input = widget.querySelector('[data-kp-points-input]');
      const applyPointsButton = widget.querySelector('[data-kp-apply-points]');
      const rewardSelect = widget.querySelector('[data-kp-reward-select]');
      const applyRewardButton = widget.querySelector('[data-kp-apply-reward]');
      const limitReached = setDiscountLimitState(widget, text, widget._kpDiscountCodeCount || 0);

      if (input) {
        input.disabled = Boolean(pointTransaction) || limitReached;
        input.value = pointTransaction ? String(pointTransaction.points || '') : '';
      }
      if (applyPointsButton) {
        applyPointsButton.disabled = Boolean(pointTransaction) || limitReached;
        if (pointTransaction && text) applyPointsButton.textContent = text.applied;
      }
      if (rewardSelect) rewardSelect.disabled = rewardApplied || limitReached;
      if (applyRewardButton) {
        applyRewardButton.disabled = rewardApplied || limitReached;
        if (rewardApplied && text) applyRewardButton.textContent = text.applied;
      }
    }
  }

  function setMessage(widget, type, message, scope) {
    const messageRegion = scope
      ? widget.querySelector('[data-kp-message-scope="' + scope + '"]')
      : widget;
    const errorEl = messageRegion?.querySelector('[data-kp-error]');
    const successEl = messageRegion?.querySelector('[data-kp-success]');

    [errorEl, successEl].forEach(function (element) {
      if (!element) return;
      setElementHidden(element, true);
      element.textContent = '';
    });

    if (!type || !message) return;
    const target = type === 'success' ? successEl : errorEl;
    if (!target) return;
    setElementHidden(target, false);
    target.textContent = message;
  }

  function setInputError(input, hasError) {
    if (!input) return;
    input.classList.toggle('kp-loyalty__input--error', Boolean(hasError));
  }

  function getStorageKey(widget, suffix) {
    return 'keptpoint:' + widget.dataset.context + ':' + widget.dataset.widgetId + ':' + suffix;
  }

  function storeCountdown(widget, expiresAt) {
    if (!expiresAt) return;
    window.localStorage.setItem(getStorageKey(widget, 'expiresAt'), expiresAt);
  }

  function buildFallbackExpiry(widget) {
    const minutes = Number(widget.dataset.countdownMinutes || 15) || 15;
    return new Date(Date.now() + minutes * 60 * 1000).toISOString();
  }

  function clearCountdown(widget) {
    window.localStorage.removeItem(getStorageKey(widget, 'expiresAt'));
  }

  function getCountdownTarget(widget) {
    const activeDiscountEl = widget.querySelector('[data-kp-active-discount]');
    const activeSubtitleEl = widget.querySelector('[data-kp-active-subtitle]');
    const countdownEl = widget.querySelector('[data-kp-countdown]');
    const expiresAt = window.localStorage.getItem(getStorageKey(widget, 'expiresAt'));
    if (!expiresAt) return null;

    return {
      activeDiscountEl: activeDiscountEl,
      activeSubtitleEl: activeSubtitleEl,
      countdownEl: countdownEl,
      expiresAt: expiresAt
    };
  }

  function startCountdown(widget, onExpired) {
    const target = getCountdownTarget(widget);
    if (widget._kpTimer) window.clearInterval(widget._kpTimer);
    if (widget._kpAppliedTimer) window.clearInterval(widget._kpAppliedTimer);

    function updateAppliedCountdowns() {
      let hasExpired = false;
      widget.querySelectorAll('[data-kp-applied-countdown][data-kp-expires-at]').forEach(function (element) {
        const remaining = new Date(element.dataset.kpExpiresAt).getTime() - Date.now();
        element.textContent = formatCountdown(remaining);
        element.classList.toggle('kp-loyalty__applied-countdown--expired', remaining <= 0);
        if (remaining <= 0) hasExpired = true;
      });
      if (hasExpired && onExpired && !widget._kpExpiryCleanupPromise) {
        window.clearInterval(widget._kpAppliedTimer);
        widget._kpExpiryCleanupPromise = Promise.resolve(onExpired())
          .catch(function (error) { console.warn('Keptpoint discount expiry cleanup error:', error); })
          .finally(function () { widget._kpExpiryCleanupPromise = null; });
      }
    }

    widget._kpAppliedTimer = window.setInterval(updateAppliedCountdowns, 1000);
    updateAppliedCountdowns();

    if (!target) return;

    const expiryTime = new Date(target.expiresAt).getTime();
    if (Number.isNaN(expiryTime)) {
      clearCountdown(widget);
      return;
    }

    function tick() {
      const remaining = expiryTime - Date.now();
      if (remaining <= 0) {
        clearCountdown(widget);
        if (target.countdownEl) target.countdownEl.textContent = '00:00';
        setElementHidden(target.activeDiscountEl, true);
        if (target.activeSubtitleEl && !target.activeSubtitleEl.textContent) {
          target.activeSubtitleEl.textContent = '';
        }
        window.clearInterval(widget._kpTimer);
        return;
      }

      setElementHidden(target.activeDiscountEl, false);
      if (target.countdownEl) target.countdownEl.textContent = formatCountdown(remaining);
    }

    tick();
    widget._kpTimer = window.setInterval(tick, 1000);
  }

  function renderWallet(widget, state, text) {
    const wallet = widget.querySelector('[data-kp-wallet-list]');
    const empty = widget.querySelector('[data-kp-wallet-empty]');
    const select = widget.querySelector('[data-kp-reward-select]');
    const applyButton = widget.querySelector('[data-kp-apply-reward]');
    if (!wallet || !empty || !select) return;

    select.innerHTML = '';
    if (!state.coupons.length) {
      setElementHidden(empty, false);
      setElementHidden(wallet, true);
      return;
    }

    setElementHidden(empty, true);
    setElementHidden(wallet, false);
    state.coupons.forEach(function (coupon) {
      const option = document.createElement('option');
      option.value = coupon.code;
      option.textContent = coupon.title + (coupon.discountText ? ' - ' + coupon.discountText : '');
      if (coupon.isApplied) option.selected = true;
      select.appendChild(option);
    });

    const hasAppliedReward = state.appliedTransactions.some(function (transaction) {
      return transaction.kind !== 'point';
    });
    if (applyButton) {
      applyButton.textContent = hasAppliedReward ? text.applied : text.apply;
      applyButton.disabled = hasAppliedReward || widget.dataset.discountLimitReached === 'true';
    }
    select.disabled = hasAppliedReward || widget.dataset.discountLimitReached === 'true';
  }

  function renderAppliedDiscounts(widget, state, text) {
    const section = widget.querySelector('[data-kp-applied-discounts]');
    const list = widget.querySelector('[data-kp-applied-list]');
    if (!section || !list) return;

    const limitReached = widget.dataset.discountLimitReached === 'true';
    list.innerHTML = '';
    state.appliedDiscounts.forEach(function (discount) {
      const item = document.createElement('li');
      item.className = 'kp-loyalty__applied-item';
      item.dataset.discountCode = discount.code;
      item.dataset.refId = discount.refId || '';

      const copy = document.createElement('div');
      copy.className = 'kp-loyalty__applied-copy';

      const code = document.createElement('p');
      code.className = 'kp-loyalty__applied-code';
      code.textContent = discount.code;
      copy.appendChild(code);

      const expiresAt = discount.transaction?.expiresAt || '';
      if (expiresAt) {
        const reservation = document.createElement('p');
        reservation.className = 'kp-loyalty__applied-reservation';
        reservation.textContent = text.reservedFor;

        const countdown = document.createElement('strong');
        countdown.className = 'kp-loyalty__applied-countdown';
        countdown.dataset.kpAppliedCountdown = '';
        countdown.dataset.kpExpiresAt = expiresAt;
        countdown.textContent = formatCountdown(new Date(expiresAt).getTime() - Date.now());
        reservation.appendChild(document.createTextNode(' '));
        reservation.appendChild(countdown);
        copy.appendChild(reservation);
      }

      const actionButton = document.createElement('button');
      actionButton.className = 'kp-loyalty__button kp-loyalty__button--secondary';
      actionButton.type = 'button';
      if (discount.isApplied) {
        actionButton.dataset.kpCancelDiscount = discount.code;
        actionButton.textContent = text.cancel;
      } else {
        actionButton.dataset.kpApplyDiscount = discount.code;
        actionButton.textContent = text.apply;
        actionButton.disabled = limitReached;
      }
      actionButton.setAttribute('aria-label', actionButton.textContent + ' ' + discount.code);

      item.appendChild(copy);
      item.appendChild(actionButton);
      list.appendChild(item);
    });

    setElementHidden(section, !state.appliedDiscounts.length);
  }

  function renderState(widget, state, text, options) {
    const sections = options?.sections || ['points', 'rewards', 'active'];
    const renderPoints = sections.includes('points');
    const renderRewards = sections.includes('rewards');
    const points = Number(state.points) || 0;
    const expiring = Number(state.expiringPoints) || 0;
    const historyLink = widget.querySelector('[data-kp-history-link]');
    const input = widget.querySelector('[data-kp-points-input]');
    const applyPointsButton = widget.querySelector('[data-kp-apply-points]');
    const expiringWrap = widget.querySelector('[data-kp-expiring-wrap]');
    const activeDiscountEl = widget.querySelector('[data-kp-active-discount]');
    const activeTitleEl = widget.querySelector('[data-kp-active-title]');
    const activeSubtitleEl = widget.querySelector('[data-kp-active-subtitle]');
    const countdownWrap = widget.querySelector('[data-kp-countdown-wrap]');
    state.appliedDiscounts = Array.isArray(state.appliedDiscounts) ? state.appliedDiscounts : [];
    state.appliedTransactions = Array.isArray(state.appliedTransactions) ? state.appliedTransactions : [];
    const discountCount = Number(widget._kpDiscountCodeCount || 0);
    const limitReached = setDiscountLimitState(widget, text, discountCount);
    const allDiscounts = state.appliedDiscounts.concat((state.availableTransactions || []).map(function (transaction) {
      return {code: transaction.discountCode, refId: transaction.refId, transaction: transaction, isApplied: false};
    }));
    const hasAppliedPoints = state.appliedTransactions.some(function (transaction) {
      return transaction.kind === 'point';
    });
    const hasActiveDiscount = Boolean(state.activeDiscount);

    if (renderPoints) {
      widget.dataset.availablePoints = String(points);

      widget.querySelectorAll('[data-kp-points-display], [data-kp-points-available]').forEach(function (element) {
        element.textContent = formatNumber(points);
      });

      widget.querySelectorAll('[data-kp-convert-rate]').forEach(function (element) {
        element.textContent = formatNumber(widget.dataset.convertRate);
      });

      if (expiringWrap) {
        setElementHidden(expiringWrap, expiring <= 0);
        const expiringEl = widget.querySelector('[data-kp-expiring-points]');
        if (expiringEl) expiringEl.textContent = formatNumber(expiring);
      }

      if (historyLink && widget.dataset.historyUrl) {
        setElementHidden(historyLink, false);
        historyLink.href = widget.dataset.historyUrl;
      }

      if (input) {
        input.disabled = hasAppliedPoints || limitReached;
        input.value = hasAppliedPoints ? String(state.appliedTransactions.find(function (transaction) { return transaction.kind === 'point'; })?.points || '') : '';
        setInputError(input, false);
      }

      if (applyPointsButton) {
        applyPointsButton.textContent = hasAppliedPoints ? text.applied : text.apply;
        applyPointsButton.disabled = hasAppliedPoints || limitReached;
      }
    }

    if (renderRewards) renderWallet(widget, state, text);
    renderAppliedDiscounts(widget, Object.assign({}, state, {appliedDiscounts: allDiscounts}), text);

    setElementHidden(activeDiscountEl, !hasActiveDiscount);
    setElementHidden(countdownWrap, true);

    if (hasActiveDiscount) {
      if (activeTitleEl) activeTitleEl.textContent = state.activeDiscount.title || text.activeReward || text.applied;
      if (activeSubtitleEl) {
        activeSubtitleEl.textContent = state.activeDiscount.subtitle || '';
        setElementHidden(activeSubtitleEl, !activeSubtitleEl.textContent);
      }
      clearCountdown(widget);
    } else if (!hasActiveDiscount) {
      clearCountdown(widget);
      if (activeTitleEl) activeTitleEl.textContent = '';
      if (activeSubtitleEl) {
        activeSubtitleEl.textContent = '';
        setElementHidden(activeSubtitleEl, true);
      }
      if (widget._kpTimer) window.clearInterval(widget._kpTimer);
    }

    startCountdown(widget, function () {
      return loadWidget(widget, text, null, {sections: ['points', 'rewards', 'active'], silent: true})
        .then(function () { refreshCartPage(widget); });
    });
  }

  function buildRewardOrder(cart) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const subtotal = items.reduce(function (total, item) {
      return total + getOriginalLinePrice(item);
    }, 0);
    return {
      total: subtotal / 100,
      items: items.map(function (item) {
        const quantity = Number(item.quantity) || 0;
        return {
          sku: String(item.sku || item.variant_id || item.product_id || ''),
          price: getOriginalUnitPrice(item) / 100,
          quantity: quantity
        };
      }).filter(function (item) { return item.sku && item.price >= 0 && item.quantity > 0; })
    };
  }

  function buildRequestBody(widget, extra) {
    return Object.assign(
      {
        context: widget.dataset.context,
        locale: window.Shopify?.locale || 'en'
      },
      extra || {}
    );
  }

  function refreshCartPage(widget) {
    if (widget.dataset.context !== 'cart' || typeof window.onCartUpdate !== 'function') return;
    window.onCartUpdate({refreshKeptpoint: false});
  }

  async function requestJson(url, body, method) {
    const fetchOptions = {
      method: method || 'POST',
      headers: {
        Accept: 'application/json'
      }
    };

    if ((method || 'POST').toUpperCase() !== 'GET') {
      fetchOptions.headers['Content-Type'] = 'application/json';
      fetchOptions.body = JSON.stringify(body);
    }

    const response = await fetch(url, fetchOptions);

    if (!response.ok) throw new Error('Request failed: ' + response.status);
    return response.json();
  }

  async function loadWidget(widget, text, cartOverride, options) {
    const proxyUrl = widget.dataset.proxyUrl;
    const partialSections = options?.sections || null;
    const silent = Boolean(options?.silent);
    if (!silent) {
      if (partialSections) setSectionLoading(widget, partialSections, true);
      else setLoading(widget, true);
    }
    if (!proxyUrl) {
      if (!silent) {
        if (partialSections) setSectionLoading(widget, partialSections, false);
        else setLoading(widget, false);
      }
      return;
    }

    try {
      const initialCart = cartOverride || await ajaxCart.getCart();
      const currentCart = await ajaxCart.removeExpiredKeptpointDiscounts(initialCart);
      const scope = await ensureReservationScope(currentCart);
      const cart = scope.cart;
      const cartToken = getCartToken(cart);
      const reservationScopeKey = scope.reservationScopeKey;
      const sections = partialSections || ['points', 'rewards', 'active'];
      const customerId = widget.dataset.customerId || '';
      const shopDomain = window.Shopify?.shop || '';
      const observedDiscountCodes = ajaxCart.readDiscountCodes(cart);
      console.log({observedDiscountCodes})
      widget._kpDiscountCodeCount = observedDiscountCodes.length;
      const requestContext = {
        checkoutId: cartToken,
        reservationScopeKey: reservationScopeKey,
        observedDiscountCodes: observedDiscountCodes,
        observedDiscountCount: observedDiscountCodes.length,
        customerId: customerId,
        shopDomain: shopDomain
      };
      const requests = {
        points: sections.includes('points') ? requestJson(buildProxyUrl(widget, '/points'), buildRequestBody(widget, requestContext)) : Promise.resolve(null),
        rewards: sections.includes('rewards') ? requestJson(buildProxyUrl(widget, '/rewards'), buildRequestBody(widget, Object.assign({}, requestContext, {order: buildRewardOrder(cart)}))) : Promise.resolve(null),
        active: sections.includes('active') ? requestJson(buildProxyUrl(widget, '/state'), buildRequestBody(widget, requestContext)) : Promise.resolve(null)
      };
      const results = await Promise.all([requests.points, requests.rewards, requests.active]);
      const previous = widget._kpState || {};
      const pointsState = results[0] ? normalizeWallet(results[0], widget.dataset.moneyFormat) : {points: previous.points || 0, expiringPoints: previous.expiringPoints || 0, memberId: previous.memberId};
      const walletState = results[1] ? normalizeWallet(results[1], widget.dataset.moneyFormat) : {coupons: previous.coupons || [], activeDiscount: previous.activeDiscount || null};
      const transactions = (results[2]?.data?.transactions || []).filter(function (item) {
        return item.discountCode && (!item.expiresAt || Date.parse(item.expiresAt) > Date.now());
      });
      const activeTransactions = transactions.filter(function (item) {
        return item.discountCode && ajaxCart.confirmDiscountApplied(cart, item.discountCode);
      });
      const activeTransaction = activeTransactions[0] || null;
      const keptpointCoupons = ajaxCart.parseKeptpointCoupons(cart.attributes);
      const appliedDiscounts = ajaxCart.readDiscountCodes(cart).map(function (code) {
        const transaction = activeTransactions.find(function (item) {
          return String(item.discountCode).toUpperCase() === code.toUpperCase();
        });
        const coupon = keptpointCoupons.find(function (item) {
          return item.shopifyCode.toUpperCase() === code.toUpperCase();
        });
        const metadata = transaction || (coupon ? {
          refId: coupon.refId,
          discountCode: coupon.shopifyCode,
          expiresAt: coupon.expiresAt || '',
          kind: coupon.redeemCode ? 'reward' : 'point',
          points: Number(coupon.point || 0)
        } : null);
        return {code: code, refId: metadata?.refId || '', transaction: metadata, isApplied: true};
      }).filter(function (discount) { return Boolean(discount.transaction); });
      const appliedTransactions = activeTransactions.map(function (transaction) {
        return {
          kind: transaction.kind,
          points: Number(transaction.point || transaction.points || 0),
          discountCode: transaction.discountCode,
          refId: transaction.refId
        };
      });
      const coupons = walletState.coupons
        .filter(coupon => coupon.status == "using")
        .map(function (coupon) {
        return Object.assign({}, coupon, {isApplied: appliedDiscounts.some(function (discount) { return discount.code.toUpperCase() === coupon.code.toUpperCase(); })});
        });
      const appliedCoupon = coupons.find(function (coupon) { return coupon.isApplied; });
      const state = {
        points: pointsState.points,
        expiringPoints: pointsState.expiringPoints,
        memberId: pointsState.memberId || widget.dataset.memberId,
        coupons: coupons,
        appliedDiscounts: appliedDiscounts,
        appliedTransactions: appliedTransactions,
        availableTransactions: transactions.filter(function (transaction) {
          return !activeTransactions.some(function (active) { return active.refId === transaction.refId; });
        }),
        activeDiscount: activeTransaction
          ? {
              type: activeTransaction.kind === 'point' ? 'points' : 'reward',
              pointsApplied: activeTransaction.kind === 'point' ? Number(activeTransaction.point || activeTransaction.points || 0) : 0,
              title: appliedCoupon?.title || activeTransaction.discountCode,
              subtitle: appliedCoupon?.discountText || activeTransaction.discountCode,
              expiresAt: activeTransaction.expiresAt,
              refId: activeTransaction.refId,
              discountCode: activeTransaction.discountCode
            }
          : walletState.activeDiscount
      };
      widget._kpState = state;
      renderState(widget, state, text, {sections: sections});
    } catch (error) {
      console.warn('Keptpoint wallet error:', error);
      const messageScope = partialSections?.includes('points')
        ? 'points'
        : partialSections?.includes('rewards') ? 'rewards' : null;
      setMessage(widget, 'error', text.apiError, messageScope);
      if (partialSections) throw error;
    } finally {
      if (!silent) {
        if (partialSections) setSectionLoading(widget, partialSections, false);
        else setLoading(widget, false);
      }
    }
  }

  function readWidgetText(widget) {
    return {
      required: widget.getAttribute('data-text-required') || 'This is a required field',
      invalidPoints: widget.getAttribute('data-text-invalid') || 'Please enter a valid point amount',
      exceededPoints: widget.getAttribute('data-text-exceeded') || 'Points entered exceed available points',
      noPoints: widget.getAttribute('data-text-no-points') || 'No available points to redeem',
      pointsApplied: widget.getAttribute('data-text-points-applied') || 'Points selected for checkout',
      pointsCancelled: widget.getAttribute('data-text-points-cancelled') || 'Point selection removed',
      loading: widget.getAttribute('data-text-loading') || 'Loading loyalty rewards',
      couponApplied: widget.getAttribute('data-text-coupon-applied') || 'Reward selected for checkout',
      couponCancelled: widget.getAttribute('data-text-coupon-cancelled') || 'Reward selection removed',
      apiError: widget.getAttribute('data-text-api-error') || 'Unable to update loyalty rewards right now. Please try again.',
      expiresOn: widget.getAttribute('data-text-expires-on') || 'Expires on',
      activeReward: widget.getAttribute('data-text-active-reward') || 'Applied reward',
      applied: widget.getAttribute('data-text-applied') || 'Applied',
      apply: widget.getAttribute('data-text-apply') || 'Apply',
      cancel: widget.getAttribute('data-text-cancel') || 'Cancel',
      reservedFor: widget.getAttribute('data-text-reserved-for') || 'Reserved for',
      appliedDiscounts: widget.getAttribute('data-text-applied-discounts') || 'Applied discounts',
      maxDiscountCodes: widget.getAttribute('data-text-max-discount-codes') || 'You can apply up to five discount codes. Remove one to apply another.'
    };
  }

  function initWidget(widget) {
    if (widget._kpAbortController || widget._kpInitScheduled) return;
    widget._kpInitScheduled = true;
    queueMicrotask(function () {
      widget._kpInitScheduled = false;
      if (!widget.isConnected || widget._kpAbortController) return;
      widget.dataset.text = JSON.stringify(readWidgetText(widget));
      bindWidget(widget);
    });
  }

  class KeptpointLoyaltyWidget extends HTMLElement {
    connectedCallback() {
      initWidget(this);
    }

    refresh(cart) {
      const text = parseJson(this.dataset.text, readWidgetText(this));
      return loadWidget(this, text, cart, {silent: true});
    }

    disconnectedCallback() {
      if (this._kpTimer) {
        window.clearInterval(this._kpTimer);
        this._kpTimer = null;
      }
      if (this._kpAppliedTimer) {
        window.clearInterval(this._kpAppliedTimer);
        this._kpAppliedTimer = null;
      }
      if (this._kpAbortController) {
        this._kpAbortController.abort();
        this._kpAbortController = null;
      }
    }
  }

  if (!customElements.get('keptpoint-loyalty-widget')) {
    customElements.define('keptpoint-loyalty-widget', KeptpointLoyaltyWidget);
  }

  document.querySelectorAll('keptpoint-loyalty-widget[data-keptpoint-widget]').forEach(function (widget) {
    initWidget(widget);
  });

  function bindWidget(widget) {
    const text = parseJson(widget.dataset.text, {});
    const abortController = new AbortController();
    const listenerOptions = {signal: abortController.signal};
    widget._kpAbortController = abortController;

    const pointsForm = widget.querySelector('[data-kp-points-form]');
    const applyPointsButton = widget.querySelector('[data-kp-apply-points]');
    const rewardSelect = widget.querySelector('[data-kp-reward-select]');
    const applyRewardButton = widget.querySelector('[data-kp-apply-reward]');
    const customerId = widget.dataset.customerId || '';
    const shopDomain = window.Shopify?.shop || '';

    async function applyTransaction(kind, input) {
      const scope = await ensureReservationScope(await ajaxCart.getCart());
      const cart = scope.cart;
      const checkoutId = getCartToken(cart);
      const observedDiscountCodes = ajaxCart.readDiscountCodes(cart);
      const path = kind === 'point' ? '/points/apply' : '/rewards/apply';
      const body = buildRequestBody(widget, Object.assign({
        checkoutId: checkoutId,
        reservationScopeKey: scope.reservationScopeKey,
        applyRequestId: createApplyRequestId(),
        observedDiscountCodes: observedDiscountCodes,
        observedDiscountCount: observedDiscountCodes.length,
        customerId: customerId,
        shopDomain: shopDomain
      }, kind === 'reward' ? Object.assign({order: buildRewardOrder(cart)}, input) : input));
      const response = await requestJson(buildProxyUrl(widget, path), body);
      const transaction = Object.assign({}, response?.data || {}, {kind: kind}, kind === 'reward' ? {rewardCode: input.rewardCode} : {});
      if (response?.code !== '000000' || transaction?.state !== 'CODE_READY' || !transaction.refId || !transaction.discountCode) throw new Error('Reservation did not produce a discount code');
      try {
        const updatedCart = await ajaxCart.applyDiscountCode(transaction.discountCode, cart.attributes);
        if (!ajaxCart.confirmDiscountApplied(updatedCart, transaction.discountCode)) throw new Error('Discount was not confirmed in cart');
        await requestJson(buildProxyUrl(widget, '/applied/result'), {
          checkoutId: checkoutId,
          reservationScopeKey: scope.reservationScopeKey,
          reservationId: transaction.refId,
          applied: true,
          customerId: customerId,
          shopDomain: shopDomain
        });
        const appliedTransaction = await getAppliedTransaction(widget, checkoutId, scope.reservationScopeKey, transaction.refId);
        const coupons = upsertKeptpointCoupon(ajaxCart.parseKeptpointCoupons(updatedCart.attributes), Object.assign({}, appliedTransaction, kind === 'reward' ? {rewardCode: input.rewardCode} : {}));
        const attributes = Object.assign({}, ajaxCart.buildKeptpointAttributes(updatedCart.attributes, coupons), kind === 'reward' ? {
          redeemNo: transaction.discountCode
        } : {});
        return ajaxCart.updateAttributes(attributes).then(ajaxCart.syncPointLineProperties);
      } catch (error) {
        try { await ajaxCart.removeDiscountCode(transaction.discountCode, cart.attributes || {}); } catch (_removeError) { }
        try {
          await requestJson(buildProxyUrl(widget, '/applied/result'), {
            checkoutId: checkoutId,
            reservationScopeKey: scope.reservationScopeKey,
            reservationId: transaction.refId,
            applied: false,
            customerId: customerId,
            shopDomain: shopDomain
          });
        } catch (_reportError) { }
        throw error;
      }

      throw new Error('Unable to apply transaction');
    }

    async function cancelDiscount(discount) {
      const scope = await ensureReservationScope(await ajaxCart.getCart());
      const cart = scope.cart;
      const checkoutId = getCartToken(cart);
      const target = String(discount.code || '').toUpperCase();
      const remaining = ajaxCart.readDiscountCodes(cart).filter(function (code) { return code.toUpperCase() !== target; });
      const coupons = ajaxCart.parseKeptpointCoupons(cart.attributes).filter(function (coupon) {
        if (discount.refId) return coupon.refId !== discount.refId;
        return coupon.shopifyCode.toUpperCase() !== target;
      });
      const attributes = Object.assign({}, ajaxCart.buildKeptpointAttributes(cart.attributes, coupons), discount.transaction?.kind === 'reward' ? {
        redeemNo: null
      } : {});
      const updatedCart = await ajaxCart.updateDiscountCodes(remaining, attributes);
      if (ajaxCart.confirmDiscountApplied(updatedCart, discount.code)) throw new Error('Discount could not be removed');
      if (discount.refId) {
        await requestJson(buildProxyUrl(widget, '/applied/cancel'), {
          checkoutId: checkoutId,
          reservationScopeKey: scope.reservationScopeKey,
          reservationId: discount.refId,
          customerId: customerId,
          shopDomain: shopDomain
        });
      }
      return updatedCart;
    }

    function handleApplyPoints() {
      const input = widget.querySelector('[data-kp-points-input]');
      const availablePoints = Number(widget.dataset.availablePoints || 0);
      const rawPoints = input?.value.trim() || '';
      const points = Number(rawPoints);

      setMessage(widget, null, null, 'points');
      setInputError(input, false);

      if (!rawPoints) {
        setInputError(input, true);
        setMessage(widget, 'error', text.required, 'points');
        return;
      }

      if (!Number.isInteger(points) || points <= 0) {
        setInputError(input, true);
        setMessage(widget, 'error', text.invalidPoints, 'points');
        return;
      }

      if (availablePoints <= 0) {
        setInputError(input, true);
        setMessage(widget, 'error', text.noPoints, 'points');
        return;
      }

      if (points > availablePoints) {
        setInputError(input, true);
        setMessage(widget, 'error', text.exceededPoints, 'points');
        return;
      }

      setBusy(widget, true);
      applyTransaction('point', {points: points, amount: 0})
        .then(function (cart) {
          return loadWidget(widget, text, cart, {sections: ['points', 'active']});
        })
        .then(function () {
          setMessage(widget, 'success', text.pointsApplied, 'points');
          refreshCartPage(widget);
        })
        .catch(function () {
          setMessage(widget, 'error', text.apiError, 'points');
        })
        .finally(function () { setBusy(widget, false, text); });
    }

    if (applyPointsButton) {
      applyPointsButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (widget.dataset.busy !== 'true') handleApplyPoints();
      }, listenerOptions);
    }

    if (pointsForm) {
      pointsForm.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        event.preventDefault();
        event.stopPropagation();
        if (widget.dataset.busy !== 'true') handleApplyPoints();
      }, listenerOptions);
    }

    function refreshRewardState(message) {
      return function (cart) {
        return loadWidget(widget, text, cart, {sections: ['rewards', 'active']}).then(function () {
          setMessage(widget, 'success', message, 'rewards');
          refreshCartPage(widget);
        });
      };
    }

    if (applyRewardButton) {
      applyRewardButton.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        const rewardCode = rewardSelect?.value || '';
        if (!rewardCode || widget.dataset.busy === 'true') return;
        setMessage(widget, null, null, 'rewards');
        setBusy(widget, true);
        applyTransaction('reward', {rewardCode: rewardCode, amount: 0})
          .then(refreshRewardState(text.couponApplied))
          .catch(function () { setMessage(widget, 'error', text.apiError, 'rewards'); })
          .finally(function () { setBusy(widget, false, text); });
      }, listenerOptions);
    }

    const appliedDiscountList = widget.querySelector('[data-kp-applied-list]');
    if (appliedDiscountList) {
      appliedDiscountList.addEventListener('click', function (event) {
        const cancelButton = event.target.closest('[data-kp-cancel-discount]');
        const applyButton = event.target.closest('[data-kp-apply-discount]');
        if ((!cancelButton && !applyButton) || widget.dataset.busy === 'true') return;
        event.preventDefault();
        event.stopPropagation();

        if (cancelButton) {
          const discount = (widget._kpState?.appliedDiscounts || []).find(function (item) {
            return item.code.toUpperCase() === cancelButton.dataset.kpCancelDiscount.toUpperCase();
          });
          if (!discount) return;
          const messageScope = discount.transaction?.kind === 'point' ? 'points' : 'rewards';
          setMessage(widget, null, null, messageScope);
          setBusy(widget, true);
          cancelDiscount(discount)
            .then(function (cart) {
              const sections = discount.transaction?.kind === 'point' ? ['points', 'active'] : ['rewards', 'active'];
              return loadWidget(widget, text, cart, {sections: sections});
            })
            .then(function () {
              setMessage(widget, 'success', text.couponCancelled, messageScope);
              refreshCartPage(widget);
            })
            .catch(function () { setMessage(widget, 'error', text.apiError, messageScope); })
            .finally(function () { setBusy(widget, false, text); });
          return;
        }

        const discount = (widget._kpState?.availableTransactions || []).find(function (item) {
          return item.discountCode.toUpperCase() === applyButton.dataset.kpApplyDiscount.toUpperCase();
        });
        if (!discount) return;
        setBusy(widget, true);
        ajaxCart.getCart().then(ensureReservationScope).then(function (resolved) {
          const cart = resolved.cart;
          const coupons = upsertKeptpointCoupon(ajaxCart.parseKeptpointCoupons(cart.attributes), discount);
          const attributes = ajaxCart.buildKeptpointAttributes(cart.attributes, coupons);
          return ajaxCart.applyDiscountCode(discount.discountCode, attributes);
        }).then(function (updatedCart) {
          if (!ajaxCart.confirmDiscountApplied(updatedCart, discount.discountCode)) throw new Error('Discount was not confirmed in cart');
          return loadWidget(widget, text, updatedCart, {sections: ['points', 'rewards', 'active']});
        }).then(function () {
          setMessage(widget, 'success', text.couponApplied, 'rewards');
          refreshCartPage(widget);
        }).catch(function () { setMessage(widget, 'error', text.apiError, 'rewards'); })
          .finally(function () { setBusy(widget, false, text); });
      }, listenerOptions);
    }

    loadWidget(widget, text);
  }

})();
